import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Scale,
  Ruler,
  Heart,
  Percent,
} from "lucide-react";
import { API } from "@/App";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next";

const MEASUREMENT_TYPES = [
  { id: "weight", name: "Kilo", unit: "kg", icon: Scale, color: "#1B365D" },
  { id: "height", name: "Boy", unit: "cm", icon: Ruler, color: "#10B981" },
  { id: "body_fat", name: "Yağ Oranı", unit: "%", icon: Percent, color: "#F59E0B" },
  { id: "muscle_mass", name: "Kas Kütlesi", unit: "kg", icon: Activity, color: "#8B5CF6" },
  { id: "waist", name: "Bel Çevresi", unit: "cm", icon: Ruler, color: "#EC4899" },
  { id: "chest", name: "Göğüs Çevresi", unit: "cm", icon: Ruler, color: "#06B6D4" },
  { id: "arm", name: "Kol Çevresi", unit: "cm", icon: Ruler, color: "#84CC16" },
  { id: "heart_rate", name: "Nabız", unit: "bpm", icon: Heart, color: "#EF4444" },
];

export default function MyMeasurementsModule({ user }) {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "evaluations"]);

  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState("weight");
  const [memberId, setMemberId] = useState(null);

  useEffect(() => {
    if (user?.user_id) {
      fetchMemberAndMeasurements();
    }
  }, [user]);

  const fetchMemberAndMeasurements = async () => {
    try {
      // Get member info
      const memberRes = await fetch(`${API}/members/by-user/${user.user_id}`, {
        credentials: "include",
      });
      
      if (memberRes.ok) {
        const members = await memberRes.json();
        if (members.length > 0) {
          setMemberId(members[0].member_id);
          
          // Get measurements
          const measRes = await fetch(`${API}/members/${members[0].member_id}/measurements`, {
            credentials: "include",
          });
          
          if (measRes.ok) {
            setMeasurements(await measRes.json());
          }
        }
      }
    } catch (error) {
      console.error("Error fetching measurements:", error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredMeasurements = () => {
    return measurements
      .filter((m) => m.parameter === selectedType)
      .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at));
  };

  const getChartData = () => {
    return getFilteredMeasurements().map((m) => ({
      date: format(new Date(m.measured_at), "dd/MM", { locale: getDateLocale() }),
      value: m.value,
    }));
  };

  const getLatestMeasurement = (type) => {
    const typeMeasurements = measurements
      .filter((m) => m.parameter === type)
      .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at));
    return typeMeasurements[0];
  };

  const getTrend = (type) => {
    const typeMeasurements = measurements
      .filter((m) => m.parameter === type)
      .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at));
    
    if (typeMeasurements.length < 2) return null;
    
    const latest = typeMeasurements[0].value;
    const previous = typeMeasurements[1].value;
    const diff = latest - previous;
    const percentage = ((diff / previous) * 100).toFixed(1);
    
    return {
      diff: diff.toFixed(1),
      percentage,
      direction: diff > 0 ? "up" : diff < 0 ? "down" : "same",
    };
  };

  const selectedTypeInfo = MEASUREMENT_TYPES.find((t) => t.id === selectedType);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="font-heading font-bold text-xl text-navy">{t("evaluations:olcumlerim")}</h2>
        <p className="text-grey text-sm">{t("common:fizikselGelisiminiziTakipEdin")}</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {MEASUREMENT_TYPES.slice(0, 4).map((type) => {
          const latest = getLatestMeasurement(type.id);
          const trend = getTrend(type.id);
          const TypeIcon = type.icon;
          
          return (
            <Card
              key={type.id}
              className={`border-0 shadow-sm cursor-pointer transition-all ${
                selectedType === type.id ? "ring-2 ring-navy" : ""
              }`}
              onClick={() => setSelectedType(type.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${type.color}20` }}
                  >
                    <TypeIcon className="w-4 h-4" style={{ color: type.color }} />
                  </div>
                  {trend && (
                    <div className={`flex items-center text-xs ${
                      trend.direction === "up" ? "text-green-600" :
                      trend.direction === "down" ? "text-red-600" : "text-grey"
                    }`}>
                      {trend.direction === "up" ? (
                        <TrendingUp className="w-3 h-3 mr-1" />
                      ) : trend.direction === "down" ? (
                        <TrendingDown className="w-3 h-3 mr-1" />
                      ) : (
                        <Minus className="w-3 h-3 mr-1" />
                      )}
                      {trend.percentage}%
                    </div>
                  )}
                </div>
                <p className="text-xs text-grey">{type.name}</p>
                <p className="font-bold text-lg text-navy">
                  {latest ? `${latest.value} ${type.unit}` : "-"}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <selectedTypeInfo.icon className="w-4 h-4" style={{ color: selectedTypeInfo.color }} />
            {selectedTypeInfo.name} Grafiği
          </CardTitle>
        </CardHeader>
        <CardContent>
          {getChartData().length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={selectedTypeInfo.color}
                  strokeWidth={2}
                  dot={{ fill: selectedTypeInfo.color }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-grey">
              <div className="text-center">
                <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>{t("evaluations:noMeasurementsYet")}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Measurement History */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{t('members:measurementHistory')}</CardTitle>
        </CardHeader>
        <CardContent>
          {measurements.length > 0 ? (
            <div className="space-y-2">
              {measurements
                .sort((a, b) => new Date(b.measured_at) - new Date(a.measured_at))
                .slice(0, 10)
                .map((m) => {
                  const typeInfo = MEASUREMENT_TYPES.find((t) => t.id === m.parameter);
                  const TypeIcon = typeInfo?.icon || Activity;
                  
                  return (
                    <div
                      key={m.measurement_id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${typeInfo?.color || "#666"}20` }}
                        >
                          <TypeIcon className="w-4 h-4" style={{ color: typeInfo?.color || "#666" }} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{typeInfo?.name || m.parameter}</p>
                          <p className="text-xs text-grey">
                            {format(new Date(m.measured_at), "dd/MM/yyyy, HH:mm", { locale: getDateLocale() })}
                          </p>
                        </div>
                      </div>
                      <p className="font-bold text-navy">
                        {m.value} {m.unit}
                      </p>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-center py-8 text-grey">
              <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>{t("evaluations:henuzOlcumKaydinizYok")}</p>
              <p className="text-xs mt-1">{t("evaluations:antrenorunuzOlcumleriniziKaydedecektir")}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
